import React, { Component } from 'react';
import { makeStyles, withStyles, useTheme } from '@material-ui/core/styles';
import {
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    IconButton,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Tooltip,
    MenuItem,
    Box,
    Typography,
    CircularProgress,
} from '@material-ui/core';
import GlobalMessage from "../../../GlobalMessage";
import FirstPageIcon from '@material-ui/icons/FirstPage';
import UndoIcon from '@material-ui/icons/Undo';
import LastPageIcon from '@material-ui/icons/LastPage';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import DeleteIcon from '@material-ui/icons/Delete';
import RestoreIcon from '@material-ui/icons/Restore';
import AddIcon from '@material-ui/icons/Add';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { isNotEmpty } from '../../../../utils/Validator';
import { parseDate } from '../../../../utils/Utils';
import ProviderService from "../../../../Services/ProviderService";
import ConfirmationDialog from "../ConfirmationDialog";
import WarningIcon from '@material-ui/icons/Warning';

const getProviderTypes = async () => {
    try {
        const response = await ProviderService.getProviderTypes();
        return response.data;
    } catch (error) {
        console.error("Error fetching provider types:", error);
        return [];
    }
};

// Shared Dialog Component for Add
const SpecialtyDialog = ({ open, onClose, onSave, allSpecialties = [], providerId }) => {
    const [formData, setFormData] = React.useState({
        providerType: '',
        providerTypeDesc: '',
        parrShortDesc: '',
        parrLongDesc: '',
        specDesc: ''
    });

    const [error, setError] = React.useState('');
    const [providerTypes, setProviderTypes] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [availableSpecialties, setAvailableSpecialties] = React.useState([]);
    const [loadingSpecialties, setLoadingSpecialties] = React.useState(false);
    const [duplicateError, setDuplicateError] = React.useState('');

    React.useEffect(() => {
        if (open) {
            setFormData({
                providerType: '',
                providerTypeDesc: '',
                parrShortDesc: '',
                parrLongDesc: '',
                specDesc: ''
            });
            setError('');
            setDuplicateError('');
            setAvailableSpecialties([]);

            setLoading(true);
            getProviderTypes()
                .then(types => {
                    setProviderTypes(types);
                    setLoading(false);
                })
                .catch(err => {
                    console.error('Error loading provider types:', err);
                    setLoading(false);
                });
        }
    }, [open]);

    const fetchSpecialtiesByProviderType = async (providerTypeId) => {
        if (!providerTypeId && providerTypeId !== 0) return;

        setLoadingSpecialties(true);
        setAvailableSpecialties([]);
        setDuplicateError('');

        try {
            const selectedProviderType = providerTypes.find(type => type.parrId === providerTypeId);

            if (!selectedProviderType) {
                console.error('Provider type not found:', providerTypeId);
                setError('Provider type information is incomplete');
                setLoadingSpecialties(false);
                return;
            }

            const response = await ProviderService.getSpecialtiesByProviderType(
                providerId,
                providerTypeId
            );

            let specialties = response.data;

            if (!specialties || specialties.length === 0) {
                throw new Error("No specialties are available because the selected provider type is not compatible with the provider's current degree. Please select a different provider type.");
            }

            if (specialties.length > 0 && typeof specialties[0] !== 'string') {
                specialties = specialties.map(spec => spec.name || spec.specDesc || spec);
            }

            // Get current specialties for this provider type
            // Match by parrShortDesc or providerTypeDesc
            const currentSpecialties = allSpecialties
                .filter(s =>
                (s.parrShortDesc === selectedProviderType.parrShortDesc ||
                    s.providerTypeDesc === selectedProviderType.parrShortDesc)
                )
                .map(s => s.specDesc);

            // Filter out already added specialties
            const filteredSpecialties = specialties.filter(
                spec => !currentSpecialties.includes(spec)
            );

            setAvailableSpecialties(filteredSpecialties);
        } catch (error) {
            console.error('Error fetching specialties:', error);
            setError(error.message || 'Failed to load specialties for this provider type');
            setAvailableSpecialties([]);
        } finally {
            setLoadingSpecialties(false);
        }
    };

    const handleProviderTypeChange = (event) => {
        const selectedId = parseInt(event.target.value, 10);
        const selectedType = providerTypes.find(type => type.parrId === selectedId);

        if (selectedType) {
            setFormData(prev => ({
                ...prev,
                providerType: selectedId,
                providerTypeDesc: selectedType.parrShortDesc,
                parrShortDesc: selectedType.parrShortDesc,
                parrLongDesc: selectedType.parrLongDesc,
                specDesc: ''
            }));

            if (error) setError('');
            setDuplicateError('');
            fetchSpecialtiesByProviderType(selectedId);
        } else {
            console.error('Provider type not found for ID:', selectedId);
        }
    };

    const handleSpecialtyChange = (event) => {
        const selectedSpecialty = event.target.value;
        setFormData(prev => ({
            ...prev,
            specDesc: selectedSpecialty
        }));

        if (error) setError('');
        setDuplicateError('');
    };

    // Check if specialty already exists - FIXED
    const isDuplicateSpecialty = () => {
        return allSpecialties.some(
            specialty =>
                specialty.specDesc === formData.specDesc &&
                (specialty.parrShortDesc === formData.parrShortDesc ||
                    specialty.providerTypeDesc === formData.providerTypeDesc)
        );
    };

    const handleSave = () => {
        // Validate for duplicates
        if (isDuplicateSpecialty()) {
            setDuplicateError(
                `The specialty "${formData.specDesc}" is already assigned to provider type "${formData.providerTypeDesc}". Please select a different specialty.`
            );
            return;
        }

        // Pass the specialty data to onSave
        onSave({
            providerType: formData.providerType,
            providerTypeDesc: formData.providerTypeDesc,
            parrShortDesc: formData.parrShortDesc,
            specDesc: formData.specDesc
        });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>Add New Specialty</DialogTitle>

            <DialogContent>
                {error && (
                    <Box mb={2}>
                        <GlobalMessage message={error} />
                    </Box>
                )}

                {duplicateError && (
                    <Box mb={2}>
                        <GlobalMessage message={duplicateError} />
                    </Box>
                )}

                <TextField
                    fullWidth
                    select
                    label="Provider Type"
                    value={formData.providerType}
                    onChange={handleProviderTypeChange}
                    variant="outlined"
                    margin="normal"
                    required
                    disabled={loading}
                    helperText={loading ? "Loading provider types..." : ""}
                >
                    {loading ? (
                        <MenuItem disabled>
                            <Box display="flex" alignItems="center">
                                <CircularProgress size={20} style={{ marginRight: 8 }} />
                                Loading...
                            </Box>
                        </MenuItem>
                    ) : (
                        providerTypes.map(option => (
                            <MenuItem key={option.parrId} value={option.parrId}>
                                {option.parrShortDesc}
                                {option.parrLongDesc ? ` - ${option.parrLongDesc}` : ''}
                            </MenuItem>
                        ))
                    )}
                </TextField>

                <TextField
                    fullWidth
                    select
                    label="Specialty"
                    value={formData.specDesc}
                    onChange={handleSpecialtyChange}
                    variant="outlined"
                    margin="normal"
                    required
                    disabled={!formData.providerType || loadingSpecialties}
                    helperText={
                        !formData.providerType ? "Select a provider type first" :
                            loadingSpecialties ? "Loading specialties..." : ""
                    }
                >
                    {loadingSpecialties ? (
                        <MenuItem disabled>
                            <Box display="flex" alignItems="center">
                                <CircularProgress size={20} style={{ marginRight: 8 }} />
                                Loading...
                            </Box>
                        </MenuItem>
                    ) : !formData.providerType ? (
                        <MenuItem disabled>Select a provider type first</MenuItem>
                    ) : availableSpecialties.length > 0 ? (
                        availableSpecialties.map(option => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))
                    ) : (
                        <MenuItem disabled>No specialties available for this provider type</MenuItem>
                    )}
                </TextField>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} variant="outlined">
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    disabled={!formData.providerType || !formData.specDesc}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const headCells = [
    { id: 'providerTypeDesc', fid: 'providerTypeDesc', numeric: false, disablePadding: true, label: 'Provider Type', filterLable: ' Provider Type', width: '40%', sortable: true },
    { id: 'specDesc', fid: 'specDesc', numeric: false, disablePadding: true, label: 'Specialty', filterLable: ' Specialty', width: '40%', sortable: true },
    { id: 'actions', numeric: false, disablePadding: true, label: 'Actions', width: '20%' }
];

function desc(a, b, orderBy) {
    let c = a[orderBy];
    let d = b[orderBy];

    if (isNotEmpty(c)) {
        if (typeof c === "string") {
            c = c.toUpperCase();
        } else if (c instanceof Date) {
            c = parseDate(c);
        }
    } else {
        c = "";
    }

    if (isNotEmpty(d)) {
        if (typeof d === "string") {
            d = d.toUpperCase();
        } else if (d instanceof Date) {
            d = parseDate(d);
        }
    } else {
        d = "";
    }

    if (d < c) return -1;
    if (d > c) return 1;
    return 0;
}

function stableSort(array, cmp) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = cmp(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map(el => el[0]);
}

function getSorting(order, orderBy) {
    return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

function EnhancedTableHead(props) {
    const { classes, order, orderBy, onRequestSort, onRequestFilter } = props;

    const createSortHandler = property => event => {
        onRequestSort(event, property);
    };

    const onFilter = property => event => {
        onRequestFilter(event.target.value, property);
    };

    return (
        <TableHead>
            <TableRow>
                {/* Provider Type column */}
                <TableCell
                    padding="none"
                    sortDirection={orderBy === 'providerTypeDesc' ? order : false}
                    style={{ width: '40%' }}
                >
                    <TableSortLabel
                        active={orderBy === 'providerTypeDesc'}
                        direction={order}
                        onClick={createSortHandler('providerTypeDesc')}
                    >
                        Provider Type
                    </TableSortLabel>
                </TableCell>

                {/* Specialty column */}
                <TableCell
                    padding="none"
                    sortDirection={orderBy === 'specDesc' ? order : false}
                    style={{ width: '40%' }}
                >
                    <TableSortLabel
                        active={orderBy === 'specDesc'}
                        direction={order}
                        onClick={createSortHandler('specDesc')}
                    >
                        Specialty
                    </TableSortLabel>
                </TableCell>

                {/* Actions column */}
                <TableCell
                    padding="none"
                    style={{ width: '20%' }}
                >
                    Actions
                </TableCell>
            </TableRow>
            <TableRow>
                {/* Provider Type filter */}
                <TableCell
                    padding="none"
                    style={{ maxWidth: '40%', padding: 0 }}
                >
                    <TextField
                        fullWidth
                        style={{ height: '.7', margin: 0 }}
                        placeholder="Filter..."
                        margin="none"
                        variant="outlined"
                        size="small"
                        onChange={onFilter('providerTypeDesc')}
                        InputProps={{
                            style: { backgroundColor: 'transparent' }
                        }}
                    />
                </TableCell>

                {/* Specialty filter */}
                <TableCell
                    padding="none"
                    style={{ maxWidth: '40%', padding: 0 }}
                >
                    <TextField
                        fullWidth
                        style={{ height: '.7', margin: 0 }}
                        placeholder="Filter..."
                        margin="none"
                        variant="outlined"
                        size="small"
                        onChange={onFilter('specDesc')}
                        InputProps={{
                            style: { backgroundColor: 'transparent' }
                        }}
                    />
                </TableCell>

                {/* Empty cell for actions column */}
                <TableCell style={{ width: '20%' }} />
            </TableRow>
        </TableHead>
    );
}

const useStyles1 = makeStyles(theme => ({
    root: {
        flexShrink: 0,
        marginLeft: theme.spacing(2.5),
    },
}));

function TablePaginationActions(props) {
    const classes = useStyles1();
    const theme = useTheme();
    const { count, page, rowsPerPage, onChangePage } = props;

    return (
        <div className={classes.root}>
            <IconButton
                onClick={(event) => onChangePage(event, 0)}
                disabled={page === 0}
                aria-label="first page"
            >
                {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
            </IconButton>
            <IconButton
                onClick={(event) => onChangePage(event, page - 1)}
                disabled={page === 0}
                aria-label="previous page"
            >
                {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
            </IconButton>
            <IconButton
                onClick={(event) => onChangePage(event, page + 1)}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="next page"
            >
                {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
            </IconButton>
            <IconButton
                onClick={(event) => onChangePage(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1))}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="last page"
            >
                {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
            </IconButton>
        </div>
    );
}

const useStyles = theme => ({
    root: {
        width: '100%',
        marginTop: 0,
        maxWidth: '100%',
        padding: 0
    },
    paper: {
        width: '100%',
        marginBottom: theme.spacing(2),
        maxWidth: '100%',
        padding: 0
    },
    table: {
        minWidth: 450,
        width: '100%',
    },
    tableWrapper: {
        overflowX: 'auto',
        width: '100%',
        maxWidth: '100%',
        padding: 0,
        margin: 0,
    },
    visuallyHidden: {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        top: 20,
        width: 1,
    },
    actionButtons: {
        display: 'flex',
        '& > *': {
            marginLeft: theme.spacing(1),
        }
    },
    deletedRow: {
        textDecoration: 'line-through',
        color: '#888'
    },
    deletedCheckbox: {
        '& .MuiCheckbox-root.Mui-disabled': {
            color: '#888',
            opacity: 0.7
        }
    },
    activeRow: {
        textDecoration: 'none'
    },
    tableHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(1, 2),
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'center',
        gap: '16px',
        marginTop: '16px',
        marginBottom: '16px'
    },
    saveButton: {
        backgroundColor: theme.palette.primary.main,
        color: 'white'
    },
    resetButton: {
        backgroundColor: theme.palette.error.main,
        color: 'white'
    },
    unsavedChangesChip: {
        backgroundColor: theme.palette.warning.light,
        color: theme.palette.warning.contrastText,
        fontWeight: 'bold',
        marginLeft: theme.spacing(2)
    }
});

class PhysicianSpecialtyEdit extends Component {
    constructor(props) {
        super(props);

        // Initialize with provided specialties or empty array
        const initialSpecialties = props.specialties ? [...props.specialties] : [];

        // Sort the initial specialties alphabetically
        const sortedSpecialties = this.sortSpecialties(initialSpecialties);

        this.state = {
            order: 'asc',
            orderBy: 'specDesc',
            page: 0,
            rowsPerPage: 5,
            showDeleteConfirmDialog: false,
            specialtyToDelete: null,
            deletedSpecialties: new Set(),
            dialog: {
                open: false,
            },
            specialties: sortedSpecialties,
            originalSpecialties: [...sortedSpecialties],
            displaySpecialties: sortedSpecialties,
            hasChanges: false,
            changesSummaryOpen: false,
            changesSummary: {
                newSpecialties: [],
                modifiedSpecialties: [],
                deletedSpecialties: []
            },
            showSingleSpecialtyWarning: false
        };
    }

    componentDidMount() {
        const sortedSpecialties = this.sortSpecialties(
            (this.props.specialties || []).map(spec => ({
                ...spec,
                providerTypeDesc: spec.providerTypeDesc || spec.parrShortDesc
            }))
        );

        this.setState({
            specialties: sortedSpecialties,
            originalSpecialties: [...sortedSpecialties],
            displaySpecialties: sortedSpecialties
        });

        if (this.props.forwardedRef) {
            this.props.forwardedRef.current = {
                getFormData: this.getFormData
            };
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.specialties !== this.props.specialties && this.props.specialties) {
            // Sort the specialties alphabetically
            const sortedSpecialties = this.sortSpecialties(this.props.specialties);

            this.setState({
                specialties: sortedSpecialties,
                originalSpecialties: [...sortedSpecialties],
                displaySpecialties: sortedSpecialties
            });
        }
    }

    // Helper method to sort specialties alphabetically
    sortSpecialties = (specialties) => {
        return [...specialties].sort((a, b) => {
            return a.specDesc.localeCompare(b.specDesc);
        });
    }

    // Check if there are any changes compared to original data
    checkForChanges = (specialties, deletedSpecialties) => {
        const { originalSpecialties } = this.state;

        if (deletedSpecialties.size > 0) {
            return true;
        }

        if (originalSpecialties.length !== specialties.length) {
            return true;
        }

        for (let i = 0; i < specialties.length; i++) {
            const current = specialties[i];
            const original = originalSpecialties.find(s => s.specDesc === current.specDesc);

            if (!original || original.providerType !== current.providerType) {
                return true;
            }
        }

        return false;
    };

    // Get active (non-deleted) specialties
    getActiveSpecialties = () => {
        return this.state.specialties.filter(
            specialty => !this.state.deletedSpecialties.has(specialty.specDesc)
        );
    };

    // Get form data for parent component
    getFormData = () => {
        const { specialties, deletedSpecialties, originalSpecialties } = this.state;

        // Filter out deleted specialties
        const activeSpecialties = specialties.filter(
            specialty => !deletedSpecialties.has(specialty.specDesc)
        );

        const changesSummary = this.generateChangesSummary();

        return {
            pristineObject: originalSpecialties.map(spec => ({
                ...spec,
                providerTypeDesc: spec.providerTypeDesc || spec.parrShortDesc // Add this mapping
            })),
            modifiedObject: this.state.hasChanges ? {
                active: activeSpecialties.map(spec => ({
                    ...spec,
                    providerTypeDesc: spec.providerTypeDesc || spec.parrShortDesc
                })),
                changes: changesSummary
            } : null,
            isValid: true
        };
    };

    handleRequestSort = (event, property) => {
        const isDesc = this.state.orderBy === property && this.state.order === 'desc';
        this.setState({ order: isDesc ? 'asc' : 'desc', orderBy: property });
    };

    handleRequestFilter = (value, property) => {
        if (value.length < 1) {
            this.setState({ displaySpecialties: this.state.specialties });
            return true;
        }

        const filtered = this.state.specialties.filter(item => {
            const testValue = item[property] !== null ? item[property].toString().toUpperCase() : "";
            if (testValue != null) {
                return testValue.search(value.toUpperCase()) !== -1;
            }
            return false;
        });

        this.setState({
            displaySpecialties: filtered,
            page: 0
        });
    };

    handleChangePage = (event, newPage) => {
        this.setState({ page: newPage });
    };

    handleChangeRowsPerPage = event => {
        this.setState({ page: 0, rowsPerPage: parseInt(event.target.value, 10) });
    };

    handleDeleteClick = (specialty, event) => {
        event.stopPropagation();

        // Get active (non-deleted) specialties
        const activeSpecialties = this.getActiveSpecialties();

        // Check if this is the only remaining specialty
        if (activeSpecialties.length === 1) {
            // Show warning dialog instead of delete confirmation
            this.setState({
                showSingleSpecialtyWarning: true
            });
            return;
        }

        // Check if this is a newly added specialty
        const isNewSpecialty = !this.state.originalSpecialties.some(
            s => s.specDesc === specialty.specDesc
        );

        this.setState({
            showDeleteConfirmDialog: true,
            specialtyToDelete: specialty,
            isNewSpecialtyToDelete: isNewSpecialty
        });
    };

    handleCancelDelete = () => {
        this.setState({
            showDeleteConfirmDialog: false,
            specialtyToDelete: null
        });
    };

    // Handle confirm delete from ConfirmationDialog
    handleConfirmDelete = () => {
        const { specialtyToDelete, deletedSpecialties, specialties } = this.state;

        if (specialtyToDelete) {
            // Check if this is a newly added specialty
            const isNewSpecialty = !this.state.originalSpecialties.some(
                original => original.specDesc === specialtyToDelete.specDesc
            );

            if (isNewSpecialty) {
                // For new specialties, remove them completely from the list
                const updatedSpecialties = specialties.filter(
                    specialty => specialty.specDesc !== specialtyToDelete.specDesc
                );

                // Update both specialties and displaySpecialties
                this.setState({
                    showDeleteConfirmDialog: false,
                    specialtyToDelete: null,
                    specialties: updatedSpecialties,
                    displaySpecialties: updatedSpecialties,
                    hasChanges: this.checkForChanges(updatedSpecialties, deletedSpecialties)
                }, () => {
                    // Notify parent component about changes
                    if (this.props.onSectionHasChanges) {
                        this.props.onSectionHasChanges(this.state.hasChanges);
                    }
                });
            } else {
                // For existing specialties, mark as deleted
                const newDeletedSet = new Set(deletedSpecialties);
                newDeletedSet.add(specialtyToDelete.specDesc);

                const hasChanges = this.checkForChanges(specialties, newDeletedSet);

                this.setState({
                    showDeleteConfirmDialog: false,
                    specialtyToDelete: null,
                    deletedSpecialties: newDeletedSet,
                    hasChanges
                }, () => {
                    // Notify parent component about changes
                    if (this.props.onSectionHasChanges) {
                        this.props.onSectionHasChanges(hasChanges);
                    }
                });
            }
        }
    };

    handleRestoreClick = (specialty, event) => {
        event.stopPropagation();

        const { deletedSpecialties, specialties } = this.state;

        // Remove from deleted set
        const newDeletedSet = new Set(deletedSpecialties);
        newDeletedSet.delete(specialty.specDesc);

        const hasChanges = this.checkForChanges(specialties, newDeletedSet);

        this.setState({
            deletedSpecialties: newDeletedSet,
            hasChanges
        });

        // Notify parent component about changes
        if (this.props.onSectionHasChanges) {
            this.props.onSectionHasChanges(hasChanges);
        }
    };

    handleAdd = () => {
        this.setState({
            dialog: {
                open: true
            }
        });
    };

    handleCloseDialog = () => {
        this.setState({
            dialog: {
                open: false
            }
        });
    };

    handleSaveDialog = (formData) => {
        const { specialties, deletedSpecialties } = this.state;

        // Add new specialty
        const updatedSpecialties = [...specialties, formData];

        // Sort the specialties alphabetically
        const sortedSpecialties = this.sortSpecialties(updatedSpecialties);

        const hasChanges = this.checkForChanges(sortedSpecialties, deletedSpecialties);

        // Update both specialties and displaySpecialties
        this.setState({
            specialties: sortedSpecialties,
            displaySpecialties: sortedSpecialties,
            hasChanges
        });

        // Notify parent component about changes
        if (this.props.onSectionHasChanges) {
            this.props.onSectionHasChanges(hasChanges);
        }

        this.handleCloseDialog();
    };

    // Generate changes summary for review
    generateChangesSummary = () => {
        const { specialties, originalSpecialties, deletedSpecialties } = this.state;

        const summary = {
            newSpecialties: [],
            modifiedSpecialties: [],
            deletedSpecialties: []
        };

        specialties.forEach(specialty => {
            const original = originalSpecialties.find(s => s.specDesc === specialty.specDesc);

            if (!original) {
                summary.newSpecialties.push({
                    ...specialty,
                    providerTypeDesc: specialty.providerTypeDesc || specialty.parrShortDesc
                });
            } else if (original.providerType !== specialty.providerType) {
                summary.modifiedSpecialties.push({
                    original: {
                        ...original,
                        providerTypeDesc: original.providerTypeDesc || original.parrShortDesc
                    },
                    modified: {
                        ...specialty,
                        providerTypeDesc: specialty.providerTypeDesc || specialty.parrShortDesc
                    }
                });
            }
        });

        originalSpecialties.forEach(original => {
            if (deletedSpecialties.has(original.specDesc)) {
                summary.deletedSpecialties.push({
                    ...original,
                    providerTypeDesc: original.providerTypeDesc || original.parrShortDesc
                });
            }
        });

        return summary;
    };

    // Handle showing changes summary dialog
    handleShowChanges = () => {
        // Generate changes summary
        const changesSummary = this.generateChangesSummary();

        this.setState({
            changesSummary,
            changesSummaryOpen: true
        });
    };

    // Close changes summary dialog
    handleCloseChangesSummary = () => {
        this.setState({ changesSummaryOpen: false });
    };

    handleResetAll = () => {
        const { originalSpecialties } = this.state;

        // Reset to original state
        this.setState({
            specialties: [...originalSpecialties],
            displaySpecialties: [...originalSpecialties],
            deletedSpecialties: new Set(),
            hasChanges: false
        });

        // Notify parent component about discarded changes
        if (this.props.onSectionDiscardChanges) {
            this.props.onSectionDiscardChanges({
                pristineObject: originalSpecialties,
                modifiedObject: null
            });
        }

        // Notify parent that this section no longer has unsaved changes
        if (this.props.onSectionHasChanges) {
            this.props.onSectionHasChanges(false);
        }
    };

    isDeleted = (specialty) => {
        return this.state.deletedSpecialties.has(specialty.specDesc);
    };

    render() {
        const { classes, providerId } = this.props;
        const {
            order,
            orderBy,
            page,
            rowsPerPage,
            showDeleteConfirmDialog,
            specialtyToDelete,
            dialog,
            displaySpecialties,
            hasChanges,
            changesSummaryOpen,
            changesSummary,
            showSingleSpecialtyWarning
        } = this.state;

        // Calculate pagination
        const pageCount = Math.max(1, Math.ceil(displaySpecialties.length / rowsPerPage));
        const currentPage = Math.min(page, pageCount - 1);

        return (
            <div className={classes.root} style={{ width: '100%', maxWidth: '100%' }}>
                <Paper className={classes.paper} style={{ width: '100%' }}>
                    <div className={classes.tableHeader}>
                        <Box display="flex" alignItems="center">
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={this.handleAdd}
                            >
                                Add New Specialty
                            </Button>
                        </Box>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={displaySpecialties.length}
                            rowsPerPage={rowsPerPage}
                            page={currentPage}
                            onChangePage={this.handleChangePage}
                            onChangeRowsPerPage={this.handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                        />
                    </div>
                    <div className={classes.tableWrapper} style={{ width: '100%', maxWidth: '100%' }}>
                        <Table
                            className={classes.table}
                            size='small'
                            aria-label="Physician specialties table"
                            style={{ width: '100%' }}
                        >
                            <EnhancedTableHead
                                classes={classes}
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={this.handleRequestSort}
                                onRequestFilter={this.handleRequestFilter}
                                rowCount={displaySpecialties.length}
                            />
                            <TableBody>
                                {displaySpecialties.length > 0 ? (
                                    stableSort(displaySpecialties, getSorting(order, orderBy))
                                        .slice(
                                            currentPage * rowsPerPage,
                                            currentPage * rowsPerPage + rowsPerPage
                                        )
                                        .map((row, index) => {
                                            const isDeleted = this.isDeleted(row);

                                            return (
                                                <TableRow
                                                    hover
                                                    tabIndex={-1}
                                                    key={`${row.specDesc}-${index}`}
                                                    style={{
                                                        padding: '5px 20px',
                                                        height: 25,
                                                        backgroundColor: index % 2 ? '#fafafa' : 'white'
                                                    }}
                                                >
                                                    {/* Provider Type cell */}
                                                    <TableCell
                                                        className={isDeleted ? classes.deletedRow : classes.activeRow}
                                                        style={{ width: '40%' }}
                                                    >
                                                        {row.parrShortDesc || row.providerTypeDesc}
                                                    </TableCell>

                                                    {/* Specialty cell */}
                                                    <TableCell
                                                        className={isDeleted ? classes.deletedRow : classes.activeRow}
                                                        style={{ width: '40%' }}
                                                    >
                                                        {row.specDesc}
                                                    </TableCell>

                                                    {/* Actions cell */}
                                                    <TableCell style={{ width: '20%' }}>
                                                        <div className={classes.actionButtons}>
                                                            {isDeleted ? (
                                                                <Tooltip title="Restore">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(event) => this.handleRestoreClick(row, event)}
                                                                        aria-label="restore specialty"
                                                                        color="primary"
                                                                    >
                                                                        <RestoreIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            ) : (
                                                                <Tooltip title="Delete">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(event) => this.handleDeleteClick(row, event)}
                                                                        aria-label="delete specialty"
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center">
                                            No specialties found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                    <div className={classes.tableHeader}>
                        <Box display="flex" alignItems="center">
                            {/* Empty box to maintain layout */}
                        </Box>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={displaySpecialties.length}
                            rowsPerPage={rowsPerPage}
                            page={currentPage}
                            onChangePage={this.handleChangePage}
                            onChangeRowsPerPage={this.handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                        />
                    </div>

                    {/* Save and Reset Buttons */}
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="center" gap={2} mt={2} mb={2}>
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={this.handleResetAll}
                                size="large"
                                disabled={!hasChanges}
                                startIcon={<UndoIcon />}
                            >
                                Discard Changes
                            </Button>
                            <Button
                                type="button"
                                variant="contained"
                                color="primary"
                                onClick={this.handleShowChanges}
                                disabled={!hasChanges}
                                size="large"
                                startIcon={<VisibilityIcon />}
                            >
                                Review Changes
                            </Button>
                        </Box>
                    </Grid>
                </Paper>
                <Dialog
                    open={showSingleSpecialtyWarning || false}
                    onClose={() => this.setState({ showSingleSpecialtyWarning: false })}
                    aria-labelledby="single-specialty-warning-title"
                >
                    <DialogTitle id="single-specialty-warning-title">
                        <Box display="flex" alignItems="center">
                            <WarningIcon
                                style={{
                                    color: '#f57c00', // Orange warning color
                                    marginRight: '8px',
                                    fontSize: '28px'
                                }}
                            />
                            Cannot Delete Only Specialty
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Typography>
                            Requests to terminate your only remaining specialty will not be processed without the addition of a replacement specialty.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => this.setState({ showSingleSpecialtyWarning: false })}
                            color="primary"
                            variant="contained"
                        >
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>

                <ConfirmationDialog
                    open={showDeleteConfirmDialog}
                    title="Confirm Delete Specialty"
                    message={this.state.isNewSpecialtyToDelete
                        ? "Are you sure you want to delete this newly added specialty?"
                        : "Are you sure you want to delete this specialty?"}
                    detailsComponent={
                        specialtyToDelete && (
                            <>
                                {this.state.isNewSpecialtyToDelete && (
                                    <Typography variant="subtitle2" style={{ color: '#f44336', marginBottom: '8px' }}>
                                        <strong>This is a newly added specialty that hasn't been saved.</strong>
                                    </Typography>
                                )}
                                <Typography variant="subtitle2">
                                    <strong>Provider Type:</strong> {specialtyToDelete.parrShortDesc || specialtyToDelete.providerTypeDesc}
                                </Typography>
                                <Typography variant="subtitle2">
                                    <strong>Specialty:</strong> {specialtyToDelete.specDesc}
                                </Typography>
                            </>
                        )
                    }
                    onConfirm={this.handleConfirmDelete}
                    onCancel={this.handleCancelDelete}
                    confirmText="Delete"
                    confirmColor="secondary"
                    severity="error"
                />

                {/* Add Dialog */}
                <SpecialtyDialog
                    open={dialog.open}
                    onClose={this.handleCloseDialog}
                    onSave={this.handleSaveDialog}
                    allSpecialties={this.getActiveSpecialties()}
                    providerId={providerId}
                />

                {/* Changes Summary Dialog */}
                <Dialog
                    open={changesSummaryOpen}
                    onClose={this.handleCloseChangesSummary}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">Review Specialty Changes</Typography>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        {changesSummary.newSpecialties.length === 0 &&
                            changesSummary.modifiedSpecialties.length === 0 &&
                            changesSummary.deletedSpecialties.length === 0 ? (
                            <Typography>No changes to review.</Typography>
                        ) : (
                            <div>
                                {/* New Specialties Section */}
                                {changesSummary.newSpecialties.length > 0 && (
                                    <div style={{ marginBottom: '20px' }}>
                                        <Typography variant="subtitle1" style={{ fontWeight: 'bold', color: '#4caf50', marginBottom: '8px' }}>
                                            New Specialties ({changesSummary.newSpecialties.length})
                                        </Typography>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell style={{ fontWeight: 'bold' }}>Provider Type</TableCell>
                                                    <TableCell style={{ fontWeight: 'bold' }}>Specialty</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {changesSummary.newSpecialties.map((specialty, index) => (
                                                    <TableRow key={`new-${index}`} style={{ backgroundColor: '#e8f5e9' }}>
                                                        <TableCell>{specialty.parrShortDesc || specialty.providerTypeDesc}</TableCell>
                                                        <TableCell>{specialty.specDesc}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                )}

                                {/* Modified Specialties Section */}
                                {changesSummary.modifiedSpecialties.length > 0 && (
                                    <div style={{ marginBottom: '20px' }}>
                                        <Typography variant="subtitle1" style={{ fontWeight: 'bold', color: '#2196f3', marginBottom: '8px' }}>
                                            Modified Specialties ({changesSummary.modifiedSpecialties.length})
                                        </Typography>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell style={{ fontWeight: 'bold' }}>Provider Type</TableCell>
                                                    <TableCell style={{ fontWeight: 'bold' }}>Specialty</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {changesSummary.modifiedSpecialties.map((change, index) => (
                                                    <TableRow key={`mod-${index}`} style={{ backgroundColor: '#e3f2fd' }}>
                                                        <TableCell>{change.modified.parrShortDesc || change.modified.providerTypeDesc}</TableCell>
                                                        <TableCell>{change.modified.specDesc}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                )}

                                {/* Deleted Specialties Section */}
                                {changesSummary.deletedSpecialties.length > 0 && (
                                    <div style={{ marginBottom: '20px' }}>
                                        <Typography variant="subtitle1" style={{ fontWeight: 'bold', color: '#f44336', marginBottom: '8px' }}>
                                            Deleted Specialties ({changesSummary.deletedSpecialties.length})
                                        </Typography>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell style={{ fontWeight: 'bold' }}>Provider Type</TableCell>
                                                    <TableCell style={{ fontWeight: 'bold' }}>Specialty</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {changesSummary.deletedSpecialties.map((specialty, index) => (
                                                    <TableRow key={`del-${index}`} style={{ backgroundColor: '#ffebee' }}>
                                                        <TableCell>{specialty.parrShortDesc || specialty.providerTypeDesc}</TableCell>
                                                        <TableCell>{specialty.specDesc}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                )}
                            </div>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.handleCloseChangesSummary} color="primary">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        );
    }
}

// Create a wrapper component that uses forwardRef
const ForwardedPhysicianSpecialtyEdit = React.forwardRef((props, ref) => {
    return <PhysicianSpecialtyEdit {...props} forwardedRef={ref} />;
});

export default withStyles(useStyles)(ForwardedPhysicianSpecialtyEdit);